import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Dialog de création d'un client autonome (sans commande existante).
 * Appelle POST /api/clients. À la création, propose de créer directement
 * une commande pour ce client.
 */
export const NewClientDialog = ({ open, onOpenChange, onCreated, onCreateOrder }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    phone: "",
    address_line1: "",
    postal_code: "",
    city: "",
    notes: "",
  });

  const reset = () =>
    setForm({
      client_name: "",
      client_email: "",
      phone: "",
      address_line1: "",
      postal_code: "",
      city: "",
      notes: "",
    });

  const handleSave = async (createOrderAfter = false) => {
    if (!form.client_name.trim() && !form.client_email.trim()) {
      toast.error("Nom ou email requis");
      return;
    }
    try {
      setSaving(true);
      const res = await api.createClient(form);
      toast.success("Client créé");
      reset();
      onOpenChange(false);
      onCreated?.(res.data);
      if (createOrderAfter && onCreateOrder) {
        onCreateOrder(res.data);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur création client";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="new-client-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            Nouveau client
          </DialogTitle>
          <DialogDescription>
            Créez un client autonome, sans commande existante. Vous pourrez lui rattacher
            des commandes dès sa création.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="new-client-name">Nom du client *</Label>
            <Input
              id="new-client-name"
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              placeholder="M. Dupont"
              data-testid="new-client-name"
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-client-email">Email</Label>
            <Input
              id="new-client-email"
              type="email"
              value={form.client_email}
              onChange={(e) => setForm({ ...form, client_email: e.target.value })}
              placeholder="contact@exemple.fr"
              data-testid="new-client-email"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-client-phone">Téléphone</Label>
            <Input
              id="new-client-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="06 00 00 00 00"
              data-testid="new-client-phone"
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="new-client-address">Adresse</Label>
            <Input
              id="new-client-address"
              value={form.address_line1}
              onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
              placeholder="12 rue de la Paix"
              data-testid="new-client-address"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-client-postal">Code postal</Label>
            <Input
              id="new-client-postal"
              value={form.postal_code}
              onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
              placeholder="75008"
              data-testid="new-client-postal"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-client-city">Ville</Label>
            <Input
              id="new-client-city"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              placeholder="Paris"
              data-testid="new-client-city"
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="new-client-notes">Notes</Label>
            <Textarea
              id="new-client-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Notes internes…"
              data-testid="new-client-notes"
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          * Nom ou email requis. Si une ville est saisie, le client sera automatiquement géolocalisé.
        </p>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} disabled={saving}>
            Annuler
          </Button>
          {onCreateOrder && (
            <Button
              variant="outline"
              onClick={() => handleSave(true)}
              disabled={saving}
              data-testid="new-client-save-and-order"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Enregistrer + créer une commande
            </Button>
          )}
          <Button
            onClick={() => handleSave(false)}
            disabled={saving}
            data-testid="new-client-save"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewClientDialog;
